<template>
  <!-- Backdrop overlay (mobile only) -->
  <transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpened"
      class="fixed inset-0 z-105 bg-onyx/50 xl:hidden"
      aria-hidden="true"
      @click="close"
    />
  </transition>

  <!-- Sidebar -->
  <aside
    id="navigation__sidebar-wrapper"
    data-testid="sidebar"
    class="fixed top-0 left-0 z-110 h-dvh w-full flex flex-col bg-depth-0 shadow-xl transition-transform duration-300 md:w-80 xl:z-1030 xl:translate-x-0"
    :class="isOpened ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'"
    :aria-hidden="!isOpened && !isDesktop"
  >
    <!-- Scrollable content area -->
    <div class="grow overflow-y-auto py-8 pl-2 pr-6 xl:pl-8">
      <div class="flex items-start">
        <!-- Close button (mobile only) -->
        <button
          class="mr-2 p-2 text-rhino xl:hidden"
          type="button"
          aria-label="Close Sidebar"
          @click="close"
        >
          <fa-icon :icon="faTimes" class="size-5" />
        </button>

        <!-- Menu column -->
        <div class="grow pl-2 xl:pl-0">
          <nuxt-link
            :to="homepageUrl"
            class="mb-4 flex justify-center md:justify-start"
            :aria-label="t('general/menu/back-to-home')"
          >
            <common-gportal-logo
              data-testid="main-logo"
              class="h-5 w-auto xl:h-8 xl:w-50"
            />
          </nuxt-link>

          <!-- Hide the wrapper too: keeping it visible while the button is
               `xl:hidden` leaves an empty 2rem (`mb-8`) gap on desktop
               landing-page sidebars. -->
          <div
            data-testid="sidebar-nav-rent-server"
            class="mb-8"
            :class="{ 'xl:hidden': isLandingpageRoute }"
          >
            <common-button :to="indexUrl" size="lg" class="min-w-50">
              {{ t('general/navigation/rent_server') }}
            </common-button>
          </div>

          <nav aria-label="Main navigation">
            <lazy-navigation-sidebar-secondlevel-profile
              v-if="authStore.isAuthenticated && isProfileRoute"
            />
            <lazy-navigation-sidebar-firstlevel v-else />
          </nav>
        </div>
      </div>
    </div>

    <!-- Quick menu (bottom) -->
    <navigation-sidebar-quickmenu class="py-4" />
  </aside>
</template>

<script setup lang="ts">
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { useMobileBehaviour as useSidebarMobileBehaviour } from './features/useMobileBehaviour';
import useNavigationBasePages from './features/useNavigationBasePages';
import useSecondLevelNavigation from './features/useSecondLevelNavigation';

const props = defineProps<{
  isOpened: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:isOpened', val: boolean): void;
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const breakpoints = useAppBreakpoints();
const isDesktop = breakpoints.greater('xl');

const { indexUrl, homepageUrl } = useNavigationBasePages();
const { isProfileRoute, isLandingpageRoute } = useSecondLevelNavigation();

const close = () => emit('update:isOpened', false);

useSidebarMobileBehaviour(toRef(props, 'isOpened'), emit);
</script>
